/**
 * Recipes — menu-item register with allergen declarations, plus
 * **instant-print recipe tiles** for the prep line.
 *
 * The list is rendered as a grid of taller "recipe cards" — each has a
 * dominant Print button that opens a browser popup with an A4 method-
 * card ready to print. Managers hang the print on the prep station so
 * cooks work from the current recipe (not the one in someone's head).
 *
 * The New-recipe form is collapsed into a dialog so the tile grid is
 * the star of the page.
 */
import { useEffect, useMemo, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import HelpButton from "@/components/HelpButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChefHat, Trash2, Printer, Plus, Search, Pencil, Tag, LayoutTemplate, Tags, ArrowRight } from "lucide-react";
import { ALLERGENS, allergenLabel } from "@/lib/allergens";
import { AllergenDetectWarning } from "@/components/AllergenDetectWarning";
import { IngredientTemplates } from "@/components/IngredientTemplates";
import { NutritionEditor } from "@/components/NutritionEditor";
import { EMPTY_NUTRITION, cleanNutrition } from "@/lib/nutrition";
import CanvasLabelDesigner from "@/components/workflow/CanvasLabelDesigner";
import { usePrinter } from "@/contexts/PrintContext";
import { buildLabelVerifyUrl } from "@/lib/labelGs1";
import { enqueuePrint } from "@/lib/printHub";

const BLANK_FORM = {
  name: "", category: "", ingredients: "", method: "",
  allergens: [], may_contain: [], shelf_life_hours: 48,
  cost_per_portion: 0, sell_price: 0, portion_size: "",
  nutrition: { ...EMPTY_NUTRITION },
};

export default function Recipes() {
  const { activeSite, refresh } = useSite();
  const { user } = useAuth();
  const { printRecipeCards } = usePrinter();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role !== "auditor";

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [designerOpen, setDesignerOpen] = useState(false);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  // `editing` doubles as "the recipe currently loaded into the form
  // dialog". `null` = the form is closed; `{}` = new recipe; `{...}` =
  // edit existing.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  // Food-label print dialog — separate from the recipe edit dialog so
  // staff can print a label without touching the recipe definition.
  const [labelFor, setLabelFor] = useState(null);   // recipe
  const [labelForm, setLabelForm] = useState({ name: "", quantity: "1 portion", storage: "refrigerated", notes: "" });
  const [labelBusy, setLabelBusy] = useState(false);

  const load = async () => {
    if (!activeSite) return;
    const { data } = await api.get("/recipes", withSite(activeSite.id));
    setItems(data);
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [activeSite]);

  const openNew = () => {
    setEditing({}); setForm(BLANK_FORM);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "",
      category: r.category || "",
      ingredients: r.ingredients || "",
      method: r.method || "",
      allergens: [...(r.allergens || [])],
      may_contain: [...(r.may_contain || [])],
      shelf_life_hours: r.shelf_life_hours ?? 48,
      cost_per_portion: r.cost_per_portion ?? 0,
      sell_price: r.sell_price ?? 0,
      portion_size: r.portion_size || "",
      nutrition: r.nutrition ? { ...EMPTY_NUTRITION, ...r.nutrition } : { ...EMPTY_NUTRITION },
    });
  };
  const close = () => { setEditing(null); setForm(BLANK_FORM); };

  const toggle = (key, id) => {
    setForm((f) => {
      const has = f[key].includes(id);
      return { ...f, [key]: has ? f[key].filter((x) => x !== id) : [...f[key], id] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        nutrition: form.nutrition?.enabled ? cleanNutrition(form.nutrition) : null,
      };
      if (editing && editing.id) {
        await api.put(`/recipes/${editing.id}`, body, withSite(activeSite.id));
      } else {
        await api.post("/recipes", body, withSite(activeSite.id));
      }
      toast.success(editing?.id ? "Recipe updated" : "Recipe saved");
      close();
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this recipe? This can't be undone.")) return;
    await api.delete(`/recipes/${id}`);
    toast.success("Deleted");
    load();
  };

  const renameCategory = async (from, to) => {
    const src = (from || "").trim();
    const dst = (to || "").trim();
    if (!dst || dst === src) return;
    try {
      const { data } = await api.post("/recipes/rename-category", { from_category: src, to_category: dst }, withSite(activeSite.id));
      const existed = categories.some((c) => c.toLowerCase() === dst.toLowerCase());
      toast.success(`${existed ? "Merged" : "Renamed"} “${src}” → “${dst}” (${data.updated} recipe${data.updated === 1 ? "" : "s"})`);
      if (activeCat === src.toLowerCase()) setActiveCat(dst.toLowerCase());
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't rename category");
    }
  };

  const printOne = (r) => {
    // Browser-print via the PrintOverlay portal — same
    // window.print()-on-current-page trick used for labels and
    // equipment barcodes. No popup, no popup-blocker dance.
    printRecipeCards({ recipes: [r], siteName: activeSite?.name || "" });
  };

  const openLabel = (r) => {
    setLabelFor(r);
    setLabelForm({ name: r.name || "", quantity: "1 portion", storage: "refrigerated", notes: "" });
  };
  const closeLabel = () => { setLabelFor(null); setLabelBusy(false); };

  const queueLabel = async () => {
    if (!labelFor || !activeSite) return;
    setLabelBusy(true);
    try {
      const now = new Date();
      const shelfMs = (labelFor.shelf_life_hours || 24) * 3600 * 1000;
      const prep = now.toISOString();
      const useBy = new Date(now.getTime() + shelfMs).toISOString();
      // Auto-mint a short human-readable batch: recipe initials + HHMM.
      const initials = (labelFor.name || "R")
        .split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 3) || "R";
      const stamp = `${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
      const batch = `${initials}-${stamp}`;
      const body = {
        product_name: (labelForm.name || labelFor.name || "").trim() || labelFor.name,
        batch_number: batch,
        prep_date: prep,
        use_by_date: useBy,
        storage: labelForm.storage,
        allergens: labelFor.allergens || [],
        quantity: labelForm.quantity,
        notes: labelForm.notes,
      };
      // Persist so the label appears in the audit trail and Verify Label
      // deep-link works — same POST the LabelPrinter page uses.
      const { data } = await api.post("/labels", body, withSite(activeSite.id));
      const label = data || body;
      const fmt = (iso) => new Date(iso).toLocaleString("en-AU", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const qrUrl = buildLabelVerifyUrl(
        { ...body, id: label.id }, process.env.REACT_APP_BACKEND_URL || "",
      );
      // Enqueue to the Print Hub — the master station renders + prints it,
      // so this works from any device (incl. iPhones with no WebUSB).
      const payload = {
        label_type: "docket",
        product_name: body.product_name,
        quantity: body.quantity,
        prep_date: fmt(prep),
        use_by_date: fmt(useBy),
        batch_number: body.batch_number,
        storage: body.storage,
        user_name: user?.name || user?.email || "",
        allergens: body.allergens,
        notes: body.notes,
        qr_url: qrUrl,
        copies: 1,
      };
      await enqueuePrint(activeSite.id, {
        label_type: "docket", product_name: body.product_name, copies: 1, payload,
      });
      toast.success(`Sent ${body.product_name} to Print Hub`);
      closeLabel();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't queue label");
      setLabelBusy(false);
    }
  };

  const categories = useMemo(() => {
    const seen = new Map();
    items.forEach((r) => {
      const c = (r.category || "").trim();
      if (!c) return;
      const key = c.toLowerCase();
      if (!seen.has(key)) seen.set(key, c);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((r) => {
      if (activeCat !== "all" && (r.category || "").trim().toLowerCase() !== activeCat) return false;
      if (!needle) return true;
      return [r.name, r.category, r.ingredients, r.method,
        ...(r.allergens || []).map(allergenLabel),
        ...(r.may_contain || []).map(allergenLabel),
      ].join(" ").toLowerCase().includes(needle);
    });
  }, [items, q, activeCat]);

  return (
    <div className="animate-fade-in" data-testid="recipes-page">
      <PageHeader
        title="Recipes"
        subtitle="Every menu item as a printable tile — tap Print to hang the method card at the prep station."
        action={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                data-testid="re-design-label"
                variant="outline"
                onClick={() => setDesignerOpen(true)}
                className="h-11 gap-2 border-eucalyptus-900 text-eucalyptus-900 hover:bg-eucalyptus-100"
              >
                <LayoutTemplate className="w-4 h-4" /> Design label
              </Button>
            )}
            {isAdmin && categories.length > 0 && (
              <Button
                data-testid="re-manage-cats"
                variant="outline"
                onClick={() => setCatMgrOpen(true)}
                className="h-11 gap-2 border-eucalyptus-900 text-eucalyptus-900 hover:bg-eucalyptus-100"
              >
                <Tags className="w-4 h-4" /> Categories
              </Button>
            )}
            {canEdit && (
              <Button
                data-testid="re-new"
                onClick={openNew}
                className="h-11 gap-2 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
              >
                <Plus className="w-4 h-4" /> New recipe
              </Button>
            )}
            <HelpButton topic="labels" label="Recipes help" />
          </div>
        }
      />

      <div className="surface p-4 mb-4 max-w-xl">
        <div className="flex items-center gap-3">
          <Search className="w-5 h-5 text-eucalyptus-700" />
          <Input
            data-testid="re-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, allergen or ingredient…"
            className="h-11 border-0 shadow-none focus-visible:ring-0 px-0 font-medium"
          />
        </div>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="re-category-chips">
          <button
            type="button"
            data-testid="re-cat-all"
            onClick={() => setActiveCat("all")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              activeCat === "all"
                ? "bg-eucalyptus-900 text-white"
                : "bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-200"
            }`}
          >
            All <span className="opacity-60">({items.length})</span>
          </button>
          {categories.map((c) => {
            const key = c.toLowerCase();
            const count = items.filter((r) => (r.category || "").trim().toLowerCase() === key).length;
            return (
              <button
                key={key}
                type="button"
                data-testid={`re-cat-${key.replace(/\s+/g, "-")}`}
                onClick={() => setActiveCat(activeCat === key ? "all" : key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  activeCat === key
                    ? "bg-eucalyptus-900 text-white"
                    : "bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-200"
                }`}
              >
                {c} <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {items.length === 0 ? (
        <div className="surface p-10 text-center text-charcoal-soft">
          No recipes yet.{" "}
          {canEdit && (
            <button onClick={openNew} className="underline text-eucalyptus-900">
              Add your first
            </button>
          )}
          .
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="re-tile-grid">
          {filtered.map((r) => (
            <RecipeTile
              key={r.id}
              recipe={r}
              onPrint={() => printOne(r)}
              onLabel={canEdit ? () => openLabel(r) : null}
              onEdit={canEdit ? () => openEdit(r) : null}
              onDelete={isAdmin ? () => del(r.id) : null}
            />
          ))}
          {filtered.length === 0 && (q || activeCat !== "all") && (
            <div className="surface p-8 text-center text-charcoal-soft sm:col-span-2 lg:col-span-3" data-testid="re-no-results">
              No recipes match{q ? ` “${q}”` : ""}{q && activeCat !== "all" ? " in" : ""}{activeCat !== "all" ? ` this category` : ""}.
            </div>
          )}
        </div>
      )}

      {/* Edit / new dialog */}
      {canEdit && (
        <Dialog open={!!editing} onOpenChange={(v) => { if (!v) close(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="re-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editing?.id ? "Edit recipe" : "New recipe"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-5 pt-2">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input data-testid="re-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" required />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input data-testid="re-cat" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-12"
                  placeholder="e.g. Main, Dessert, Sauce" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Ingredients</Label>
                <Textarea data-testid="re-ing" rows={4} value={form.ingredients}
                  onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                  placeholder="One per line, e.g.&#10;500g chicken thigh&#10;2 tbsp olive oil" />
                <IngredientTemplates
                  siteId={activeSite?.id}
                  value={form.ingredients}
                  onApply={(text) => setForm((f) => ({ ...f, ingredients: text }))}
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Method</Label>
                <Textarea data-testid="re-method" rows={4} value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}
                  placeholder="One step per line" />
              </div>

              <div className="space-y-3">
                <Label>Contains allergens</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ALLERGENS.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        data-testid={`re-allergen-${a.id}`}
                        checked={form.allergens.includes(a.id)}
                        onCheckedChange={() => toggle("allergens", a.id)}
                      />
                      {a.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>May contain (cross-contact risk)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ALLERGENS.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        data-testid={`re-maycontain-${a.id}`}
                        checked={form.may_contain.includes(a.id)}
                        onCheckedChange={() => toggle("may_contain", a.id)}
                      />
                      {a.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <AllergenDetectWarning
                  ingredients={form.ingredients}
                  declared={form.allergens}
                  mayContain={form.may_contain}
                  onAddContains={(id) => setForm((f) => ({ ...f, allergens: [...new Set([...f.allergens, id])] }))}
                  onAddMayContain={(id) => setForm((f) => ({ ...f, may_contain: [...new Set([...f.may_contain, id])] }))}
                  onAddAllContains={(ids) => setForm((f) => ({ ...f, allergens: [...new Set([...f.allergens, ...ids])] }))}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Shelf life (hours)</Label>
                <Input data-testid="re-shelf" type="number" min="1" value={form.shelf_life_hours}
                  onChange={(e) => setForm({ ...form, shelf_life_hours: parseInt(e.target.value || 0) })}
                  className="h-12 w-40" />
              </div>

              {/* Menu engineering — optional cost fields. When both cost
                  and sell price are set, the tile shows a food-cost %
                  badge and unlocks the "Cost card" print variant. */}
              <div className="md:col-span-2 border-t border-[color:var(--border)] pt-4">
                <div className="label-eyebrow mb-3">Menu engineering (optional)</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Ingredient cost / portion (AUD)</Label>
                    <Input data-testid="re-cost" type="number" step="0.01" min="0"
                      value={form.cost_per_portion}
                      onChange={(e) => setForm({ ...form, cost_per_portion: parseFloat(e.target.value || 0) })}
                      className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label>Menu sell price (AUD)</Label>
                    <Input data-testid="re-sell" type="number" step="0.01" min="0"
                      value={form.sell_price}
                      onChange={(e) => setForm({ ...form, sell_price: parseFloat(e.target.value || 0) })}
                      className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label>Portion size</Label>
                    <Input data-testid="re-portion" value={form.portion_size}
                      onChange={(e) => setForm({ ...form, portion_size: e.target.value })}
                      className="h-12" placeholder="e.g. 1 bowl, 220g" />
                  </div>
                </div>
              </div>

              {/* Nutrition Information Panel — autofills onto docket labels
                  when this recipe is selected in the Label Printer. */}
              <div className="md:col-span-2 border-t border-[color:var(--border)] pt-4">
                <NutritionEditor
                  value={form.nutrition}
                  onChange={(n) => setForm((f) => ({ ...f, nutrition: n }))}
                />
              </div>

              <DialogFooter className="md:col-span-2 pt-4">
                <Button
                  type="button" variant="ghost" onClick={close}
                  data-testid="re-cancel" className="h-11"
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="re-submit"
                  className="h-11 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white">
                  {editing?.id ? "Save changes" : "Save recipe"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      {/* Print food-label dialog — quantity + storage. Auto-computes
          use-by from the recipe's shelf_life_hours. */}
      <Dialog open={!!labelFor} onOpenChange={(v) => { if (!v) closeLabel(); }}>
        <DialogContent className="max-w-md" data-testid="re-label-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Print label · {labelFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-xs text-charcoal-soft">
              We&apos;ll stamp prep time as now, compute use-by as{" "}
              <span className="font-mono font-bold">{labelFor?.shelf_life_hours ?? 24}h</span>{" "}
              from now, and copy the declared allergens onto the label.
            </div>
            <div className="space-y-2">
              <Label>Label text</Label>
              <Input
                data-testid="re-label-name"
                value={labelForm.name}
                onChange={(e) => setLabelForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Edit for one-off / made-to-order items"
                className="h-11 font-medium"
              />
              <p className="text-[11px] text-charcoal-soft">Defaults to the recipe name — edit it for a special made-to-order label.</p>
            </div>
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                data-testid="re-label-quantity"
                value={labelForm.quantity}
                onChange={(e) => setLabelForm((f) => ({ ...f, quantity: e.target.value }))}
                placeholder="e.g. 4 portions, 2kg"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label>Storage</Label>
              <Select
                value={labelForm.storage}
                onValueChange={(v) => setLabelForm((f) => ({ ...f, storage: v }))}
              >
                <SelectTrigger data-testid="re-label-storage" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="refrigerated">Refrigerated (≤5°C)</SelectItem>
                  <SelectItem value="frozen">Frozen (≤-18°C)</SelectItem>
                  <SelectItem value="ambient">Ambient / dry store</SelectItem>
                  <SelectItem value="hot_holding">Hot holding (≥60°C)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                data-testid="re-label-notes"
                rows={2}
                value={labelForm.notes}
                onChange={(e) => setLabelForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Anything the next cook needs to know"
              />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="ghost" onClick={closeLabel} data-testid="re-label-cancel" className="h-11">
              Cancel
            </Button>
            <Button
              onClick={queueLabel}
              disabled={labelBusy}
              data-testid="re-label-print"
              className="h-11 gap-2 bg-ochre-600 hover:bg-ochre-400 text-white"
            >
              <Printer className="w-4 h-4" />
              {labelBusy ? "Preparing…" : "Batch print"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Label Designer — the SAME canvas designer as Product Library,
          sharing the site's saved layouts. Opens on demand for admins. */}
      <Dialog open={designerOpen} onOpenChange={setDesignerOpen}>
        <DialogContent
          data-testid="re-designer-dialog"
          className="max-w-[96vw] w-[96vw] h-[92vh] overflow-y-auto p-4"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Label Designer</DialogTitle>
          </DialogHeader>
          {activeSite && <CanvasLabelDesigner activeSite={activeSite} refresh={refresh} />}
        </DialogContent>
      </Dialog>

      {/* Category Manager — rename or merge recipe categories in one place. */}
      <Dialog open={catMgrOpen} onOpenChange={setCatMgrOpen}>
        <DialogContent className="max-w-lg" data-testid="re-cat-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Tags className="w-5 h-5 text-eucalyptus-900" /> Manage categories
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-charcoal-soft -mt-1">
            Rename a category, or type an existing name to merge them. Every recipe in that category updates instantly.
          </p>
          <div className="space-y-2 pt-2 max-h-[55vh] overflow-y-auto">
            {categories.map((c) => (
              <CategoryRow key={c.toLowerCase()} category={c} count={items.filter((r) => (r.category || "").trim().toLowerCase() === c.toLowerCase()).length} onRename={renameCategory} />
            ))}
            {categories.length === 0 && (
              <div className="text-sm text-charcoal-soft">No categories yet.</div>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setCatMgrOpen(false)} data-testid="re-cat-close" className="h-11">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryRow({ category, count, onRename }) {
  const [val, setVal] = useState(category);
  const dirty = val.trim() && val.trim() !== category;
  return (
    <div className="flex items-center gap-2 surface-alt p-2 rounded-lg" data-testid={`re-cat-row-${category.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="font-mono text-[11px] text-charcoal-soft w-24 shrink-0 truncate" title={category}>
        {category} <span className="opacity-60">({count})</span>
      </span>
      <ArrowRight className="w-4 h-4 text-charcoal-soft shrink-0" />
      <Input
        data-testid={`re-cat-input-${category.toLowerCase().replace(/\s+/g, "-")}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="h-9 flex-1"
      />
      <Button
        data-testid={`re-cat-save-${category.toLowerCase().replace(/\s+/g, "-")}`}
        disabled={!dirty}
        onClick={() => onRename(category, val)}
        className="h-9 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white disabled:opacity-40"
      >
        Save
      </Button>
    </div>
  );
}

function RecipeTile({ recipe, onPrint, onLabel, onEdit, onDelete }) {
  const r = recipe;
  const ingredientPreview = (r.ingredients || "").split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean).slice(0, 4);
  // Food cost %: rule of thumb ≤ 30% is healthy. Only render when the
  // manager has actually configured both cost and sell price.
  const hasCost = (r.cost_per_portion || 0) > 0 && (r.sell_price || 0) > 0;
  const foodCostPct = hasCost ? Math.round(100 * r.cost_per_portion / r.sell_price) : null;
  const costBand =
    foodCostPct === null ? "" :
    foodCostPct <= 30 ? "bg-eucalyptus-100 text-eucalyptus-900" :
    foodCostPct <= 40 ? "bg-amber-100 text-amber-900" :
    "bg-ochre-600 text-white";
  return (
    <article
      data-testid={`re-tile-${r.id}`}
      className="surface p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="label-eyebrow">{r.category || "Recipe"}</div>
          <h3 className="font-display font-extrabold text-lg mt-0.5 leading-tight truncate">
            {r.name}
          </h3>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onEdit && (
            <Button
              data-testid={`re-edit-${r.id}`}
              variant="ghost" size="icon"
              className="text-eucalyptus-900 hover:bg-eucalyptus-100"
              onClick={onEdit}
              title="Edit recipe"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {onDelete && (
            <Button
              data-testid={`re-del-${r.id}`}
              variant="ghost" size="icon"
              className="text-ochre-600 hover:bg-ochre-600/10"
              onClick={onDelete}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap gap-1.5" data-testid={`re-tile-allergens-${r.id}`}>
        {(r.allergens || []).map((a) => (
          <Badge key={a} className="bg-ochre-600 text-white hover:bg-ochre-600 font-mono text-[10px]">
            {allergenLabel(a)}
          </Badge>
        ))}
        {(r.may_contain || []).map((a) => (
          <Badge key={a} variant="outline" className="border-ochre-600/50 text-ochre-600 font-mono text-[10px]">
            may: {allergenLabel(a)}
          </Badge>
        ))}
        {!(r.allergens || []).length && !(r.may_contain || []).length && (
          <span className="text-xs text-eucalyptus-700">No declared allergens</span>
        )}
      </div>

      {ingredientPreview.length > 0 && (
        <div className="text-xs text-charcoal-soft space-y-0.5 border-t border-[color:var(--border)] pt-3">
          <div className="label-eyebrow mb-1">Ingredients</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {ingredientPreview.map((line, i) => (
              <li key={i} className="truncate">{line}</li>
            ))}
            {(r.ingredients || "").split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean).length > 4 && (
              <li className="text-charcoal-soft/70">+ more…</li>
            )}
          </ul>
        </div>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-[color:var(--border)] mt-auto">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[11px] text-charcoal-soft">
            Shelf life {r.shelf_life_hours || "—"}h
          </span>
          {foodCostPct !== null && (
            <Badge
              className={`${costBand} hover:${costBand} font-mono text-[10px]`}
              data-testid={`re-tile-cost-${r.id}`}
              title={`Food cost ${foodCostPct}% — ≤30% healthy, 31–40% watch, >40% loss`}
            >
              FC {foodCostPct}%
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {onLabel && (
            <Button
              data-testid={`re-label-${r.id}`}
              onClick={onLabel}
              variant="outline"
              className="h-9 gap-2 border-ochre-600 text-ochre-600 hover:bg-ochre-600/10"
              title="Print an 80mm food label for this recipe (prep now, auto use-by)"
            >
              <Tag className="w-4 h-4" /> Print label
            </Button>
          )}
          <Button
            data-testid={`re-print-${r.id}`}
            onClick={onPrint}
            className="h-9 gap-2 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
          >
            <Printer className="w-4 h-4" /> Print card
          </Button>
        </div>
      </footer>
    </article>
  );
}
